import React from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { ReactComponent as Chart } from '@blink/assets/src/icons/chart.svg';

import { HistoryItem } from '@/shared/api/borrow';
import {
    calculateTrend,
    formatInterval,
    formatMonth,
    transformChartData,
} from '@/shared/utils/helpersChart';

interface ApyChartProps {
    chart: HistoryItem[];
}

export const ApyChart: React.FC<ApyChartProps> = ({ chart }) => {
    const transformedChart = transformChartData(chart);
    const trend = calculateTrend(transformedChart);
    const interval = formatInterval(transformedChart);

    const renderTooltip = (props: TooltipProps<any, any>) => {
        const entry = props?.payload?.[0]?.payload;

        if (!entry) {
            return null;
        }

        const date = new Date(entry.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });

        return (
            <div className='flex flex-col items-start gap-2 p-3 rounded bg-background-fourth'>
                <div className='text-content-secondary text-sm'>{date}</div>
                <div className='text-content-secondary text-sm'>{`APY Rate: ${entry.apy}%`}</div>
            </div>
        );
    };

    return (
        <div className='flex flex-col p-7 gap-[10px]'>
            <div className='flex flex-col gap-[6px]'>
                <div className='font-semibold text-[16px]'>Historical APY Performance</div>
                <div className='font-normal text-content-secondary text-[16px]'>
                    APY rates over the past 12 months
                </div>
            </div>

            <div className='pt-[16px] h-[calc(100%-80px)]'>
                <div className='relative w-full h-full'>
                    <ResponsiveContainer width='100%' height={280}>
                        <ComposedChart data={transformedChart}>
                            <CartesianGrid vertical={false} stroke='#e5e7eb' />
                            <XAxis
                                dataKey='date'
                                tickFormatter={formatMonth}
                                tickLine={false}
                                axisLine={false}
                                padding={{ left: 10, right: 10 }}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                domain={['dataMin - 0.5', 'dataMax + 0.5']}
                                tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip content={renderTooltip} />
                            <Area
                                type='stepAfter'
                                dataKey='apy'
                                fill='#6366f1'
                                stroke='transparent'
                                fillOpacity={0.2}
                            />
                            <Line
                                type='stepAfter'
                                dataKey='apy'
                                stroke='#4f46e5'
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className='py-5 gap-3 pb-0'>
                <div className='flex w-full items-start gap-2 text-sm'>
                    <div className='grid gap-2'>
                        <div className='flex items-center font-semibold text-[14px] gap-3'>
                            {trend === 'N/A' ? (
                                <>
                                    Trend data not available this month <Chart />
                                </>
                            ) : (
                                <>
                                    Trending {parseFloat(trend) >= 0 ? 'up' : 'down'} by{' '}
                                    {parseFloat(trend)}% this month{' '}
                                    <Chart
                                        className={
                                            parseFloat(trend) < 0 ? 'transform scale-y-[-1]' : ''
                                        }
                                    />
                                </>
                            )}
                        </div>

                        <div className='font-normal text-content-secondary text-[14px]'>
                            {`Interval: ${interval}`}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
